import React, { useEffect, useState, useCallback } from "react";
import { ClipboardCheck, Plus, Warehouse, Store, CheckCircle2, ArrowLeft, Search, CalendarDays, Sparkles, RotateCcw, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, Input } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, fmtUnit, fmtWIB } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

export default function Opname() {
  const toast = useToast();
  const { isRole, user } = useAuth();
  const { t } = useT();
  const [sessions, setSessions] = useState(null);
  const [meta, setMeta] = useState({ implementation_mode: false, opening_done: {} });
  const [branches, setBranches] = useState([]);
  const [active, setActive] = useState(null); // detail session
  const [counts, setCounts] = useState({}); // product_id -> {actual, reason, unit_cost}
  const [search, setSearch] = useState("");
  const [startModal, setStartModal] = useState(false);
  const [startForm, setStartForm] = useState({ scope: "central", branch_id: "", opname_type: "regular", reason: "" });
  const [pinModal, setPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const isBranch = isRole("BRANCH_STAFF", "BRANCH_MANAGER") || (isRole("KITCHEN") && user?.kitchen_type === "branch");
  const canEdit = isRole("SUPER_ADMIN", "WAREHOUSE") || isBranch;

  const loadList = useCallback(() => {
    api.get("/opname").then(({ data }) => {
      setSessions(data.sessions);
      setMeta({ implementation_mode: !!data.implementation_mode, opening_done: data.opening_done || {} });
    });
  }, []);
  useEffect(() => {
    loadList();
    api.get("/branches").then(({ data }) => setBranches(data));
  }, [loadList]);

  const openSession = async (id) => {
    const { data } = await api.get(`/opname/${id}`);
    setActive(data);
    const c = {};
    data.lines.forEach((l) => { c[l.product_id] = { actual: l.actual_qty ?? "", reason: l.reason ?? "", unit_cost: l.unit_cost ?? "" }; });
    setCounts(c);
  };

  const startOpname = async () => {
    if (!isBranch && startForm.scope === "branch" && !startForm.branch_id) return toast.error(t("Select a branch"));
    if (startForm.opname_type === "correction_reset" && !startForm.reason.trim()) return toast.error(t("Reason is required for a Correction Reset"));
    setBusy(true);
    try {
      const payload = isBranch
        ? { opname_type: startForm.opname_type, reason: startForm.reason || null }
        : { ...startForm, reason: startForm.reason || null };
      const { data } = await api.post("/opname/start", payload);
      toast.success(`${t("Stock Take")} ${data.opname_no} ${t("started")}`);
      setStartModal(false); setStartForm({ scope: "central", branch_id: "", opname_type: "regular", reason: "" });
      openSession(data.id); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const saveCounts = async () => {
    try { await api.post(`/opname/${active.id}/save`, { lines: buildLines() }); toast.success(t("Counts saved")); openSession(active.id); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const isSpecial = (s) => s && ["opening", "correction_reset"].includes(s.opname_type);

  const finalize = async () => {
    if (isSpecial(active)) { setPin(""); setPinModal(true); return; }
    const branchScope = active.scope === "branch";
    const msg = branchScope
      ? t("Finalize this stock take? Lines within ±5% variance auto-adjust stock. Lines over ±5% are sent to HQ for approval before adjusting.")
      : t("Finalize this opname? Stock will be adjusted and ledger entries created.");
    if (!window.confirm(msg)) return;
    await doFinalize(null);
  };

  const doFinalize = async (ownerPin) => {
    setBusy(true);
    try {
      await saveCountsSilent();
      const { data } = await api.post(`/opname/${active.id}/finalize`, ownerPin ? { owner_pin: ownerPin } : {});
      const extra = data.pending_approval > 0 ? `, ${data.pending_approval} ${t("sent for approval")}` : "";
      toast.success(`${t("Finalized —")} ${data.adjustments} ${t("applied")}${extra} · ${t("Accuracy")} ${data.accuracy_pct ?? "—"}%`);
      setPinModal(false);
      setActive(null); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const buildLines = () => Object.entries(counts)
    .filter(([, v]) => v.actual !== "" && v.actual !== null)
    .map(([product_id, v]) => ({
      product_id, actual_qty: Number(v.actual), reason: v.reason || null,
      unit_cost: active?.opname_type === "opening" && v.unit_cost !== "" && v.unit_cost !== null ? Number(v.unit_cost) : null,
    }));

  const saveCountsSilent = async () => {
    await api.post(`/opname/${active.id}/save`, { lines: buildLines() });
  };

  if (!sessions) return <PageLoader />;

  const TYPE_META = {
    opening: { label: t("First Opening Stock Take"), tone: "warn", icon: Sparkles },
    correction_reset: { label: t("Correction Reset Stock Take"), tone: "danger", icon: RotateCcw },
  };
  const TypeBadge = ({ type }) => {
    const m = TYPE_META[type];
    if (!m) return null;
    const I = m.icon;
    return <Badge tone={m.tone}><I size={12} /> {m.label}</Badge>;
  };

  // ---- Detail view ----
  if (active) {
    const editable = active.status === "DRAFT" && canEdit;
    const isBranchScope = active.scope === "branch";
    const isOpening = active.opname_type === "opening";
    const special = isSpecial(active);
    const lines = active.lines.filter((l) => (l.product_name || "").toLowerCase().includes(search.toLowerCase()));
    const cols = isOpening && editable ? 7 : 6;
    const subtitle = active.status === "FINALIZED" && active.accuracy_pct != null
      ? `${active.branch_name} · ${t("FINALIZED")} · ${t("Accuracy")} ${active.accuracy_pct}%`
      : `${active.branch_name} · ${t(active.status)}`;
    const bucketOf = (pc) => { const v = (pc || "").toUpperCase(); return v === "RTC" ? "rtc" : (v === "SEMI_FINISHED" || v === "FINISHED") ? "finished" : "raw"; };
    const GROUPS = [["raw", t("Raw Inventory"), "bg-amber-500"], ["rtc", t("Ready-to-Cook Inventory"), "bg-orange-500"], ["finished", t("Finished Products"), "bg-emerald-500"]];
    const renderLineRow = (l) => {
      const c = counts[l.product_id] || { actual: "", reason: "", unit_cost: "" };
      const actual = c.actual === "" ? null : Number(c.actual);
      const diff = actual === null ? null : Number((actual - l.system_qty).toFixed(2));
      const pct = (diff === null || !l.system_qty) ? null : Number((diff / l.system_qty * 100).toFixed(1));
      return (
        <tr key={l.product_id} className="border-b border-line/60" data-testid={`opname-row-${l.product_name}`}>
          <td className="px-4 py-3">
            <div className="flex items-center gap-3">
              <ProductAvatar image={l.image} name={l.product_name} size="sm" />
              <div><p className="font-semibold text-ink">{l.product_name}</p><p className="text-[11px] font-bold uppercase text-ink-mute">{fmtUnit(l.unit)}</p></div>
            </div>
          </td>
          <td className="px-4 py-3 text-right font-semibold">{fmtNum(l.system_qty)}</td>
          <td className="px-4 py-3 text-right">
            {editable ? (
              <input type="number" value={c.actual} onChange={(e) => setCounts((s) => ({ ...s, [l.product_id]: { ...c, actual: e.target.value } }))}
                className="cf-input w-24 text-right" data-testid={`opname-actual-${l.product_name}`} />
            ) : (l.actual_qty ?? "—")}
          </td>
          <td className={`px-4 py-3 text-right font-bold ${diff === null ? "text-ink-mute" : diff < 0 ? "text-danger-text" : diff > 0 ? "text-success-text" : "text-ink-soft"}`}>
            {diff === null ? "—" : `${diff > 0 ? "+" : ""}${fmtNum(diff)}`}
          </td>
          {isOpening && editable && (
            <td className="px-4 py-3 text-right">
              <input type="number" min="0" value={c.unit_cost} onChange={(e) => setCounts((s) => ({ ...s, [l.product_id]: { ...c, unit_cost: e.target.value } }))}
                className="cf-input w-28 text-right" data-testid={`opname-unitcost-${l.product_name}`} />
            </td>
          )}
          <td className="px-4 py-3 text-right text-xs">
            {pct === null ? "—" : <Badge tone={Math.abs(pct) > (isBranchScope ? 5 : 20) ? "danger" : Math.abs(pct) > 0 ? "warn" : "mute"}>{pct > 0 ? "+" : ""}{pct}%</Badge>}
            {l.pending_approval && <div className="mt-1"><Badge tone="warn">{t("Pending")}</Badge></div>}
          </td>
          <td className="px-4 py-3">
            {editable ? (
              <input value={c.reason} onChange={(e) => setCounts((s) => ({ ...s, [l.product_id]: { ...c, reason: e.target.value } }))}
                placeholder={t("Reason")} className="cf-input" data-testid={`opname-reason-${l.product_name}`} />
            ) : (l.reason || "—")}
          </td>
        </tr>
      );
    };
    return (
      <div className="animate-fade-up pb-24 lg:pb-2">
        <PageHeader title={`${t("Stock Take")} ${active.opname_no}`} subtitle={subtitle} icon={ClipboardCheck}>
          <Button variant="outline" size="sm" onClick={() => setActive(null)} data-testid="opname-back"><ArrowLeft size={16} /> {t("Back")}</Button>
          {editable && <Button variant="outline" size="sm" onClick={saveCounts} data-testid="opname-save">{t("Save Counts")}</Button>}
          {editable && <Button size="sm" onClick={finalize} loading={busy} data-testid="opname-finalize"><CheckCircle2 size={16} /> {t("Finalize")}</Button>}
        </PageHeader>

        {special && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="opname-type-banner">
            <TypeBadge type={active.opname_type} />
            {active.type_reason && <span className="text-xs text-ink-soft">"{active.type_reason}"</span>}
          </div>
        )}
        {special && editable && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="opname-special-note">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>
              {isOpening
                ? t("First Opening establishes the official opening inventory valuation. Unit cost (HPP) is editable here. Finalizing requires the Owner PIN.")
                : t("Correction Reset creates a NEW inventory baseline as of today. Historical journals and reports are never modified. Finalizing requires the Owner PIN.")}
            </span>
          </div>
        )}

        {/* Permanent baseline audit trail (finalized special sessions) */}
        {special && active.status === "FINALIZED" && active.authorization && (
          <Card className="mb-4 border-2 border-amber-200" data-testid="opname-audit-card">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700"><ShieldCheck size={14} /> {t("Inventory Baseline Record (Permanent)")}</p>
            <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <div className="flex justify-between"><span className="text-ink-soft">{t("Baseline Version")}</span><b data-testid="opname-baseline-version">v{active.baseline_version}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("Effective Date")}</span><b>{active.effective_date}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("Previous Inventory Value")}</span><b>Rp {Number(active.previous_inventory_value || 0).toLocaleString("id-ID")}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("New Inventory Value")}</span><b>Rp {Number(active.new_inventory_value || 0).toLocaleString("id-ID")}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("Difference")}</span><b className={Number(active.value_difference) < 0 ? "text-danger-text" : "text-success-text"}>Rp {Number(active.value_difference || 0).toLocaleString("id-ID")}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("Submitted By")}</span><b>{active.authorization.submitted_by}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("Authorization")}</span><b>Owner PIN · {(active.authorization.authorized_at || "").slice(0, 16).replace("T", " ")}</b></div>
              {active.authorization.reason && <div className="flex justify-between"><span className="text-ink-soft">{t("Reason")}</span><b>{active.authorization.reason}</b></div>}
            </div>
          </Card>
        )}

        {isBranchScope && editable && !special && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="opname-5pct-note">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>{t("Lines within ±5% variance adjust stock automatically. Lines over ±5% require HQ approval before stock changes.")}</span>
          </div>
        )}
        {active.pending_count > 0 && (
          <div className="mb-4 rounded-xl border border-warn/40 bg-warn-light px-4 py-3 text-sm font-medium text-warn-text" data-testid="opname-pending-banner">
            {active.pending_count} {t("line(s) awaiting HQ approval.")}
          </div>
        )}

        <div className="relative mb-4 max-w-xs">
          <Search size={18} className="absolute left-3.5 top-3 text-ink-mute" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Search products...")} className="cf-input pl-10" data-testid="opname-search" />
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-4 py-3">{t("Product")}</th><th className="px-4 py-3 text-right">{t("System")}</th>
                <th className="px-4 py-3 text-right">{t("Actual Count")}</th><th className="px-4 py-3 text-right">{t("Difference")}</th>
                {isOpening && editable && <th className="px-4 py-3 text-right">{t("Unit Cost (HPP)")}</th>}
                <th className="px-4 py-3 text-right">{t("Variance")}</th><th className="px-4 py-3">{t("Reason")}</th></tr></thead>
              <tbody>
                {GROUPS.map(([key, label, dot]) => {
                  const items = lines.filter((l) => bucketOf(l.production_class) === key);
                  if (items.length === 0) return null;
                  return (
                    <React.Fragment key={key}>
                      <tr className="bg-stone-50" data-testid={`opname-group-${key}`}>
                        <td colSpan={cols} className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${dot}`} />
                            <span className="text-[11px] font-bold uppercase tracking-wide text-ink-soft">{label}</span>
                            <span className="text-[11px] text-ink-mute">· {items.length}</span>
                          </div>
                        </td>
                      </tr>
                      {items.map(renderLineRow)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Owner Authorization dialog (First Opening / Correction Reset) */}
        <Modal open={pinModal} onClose={() => setPinModal(false)} title={t("Owner Authorization Required")} size="sm"
          footer={<>
            <Button variant="outline" onClick={() => setPinModal(false)} disabled={busy}>{t("Cancel")}</Button>
            <Button onClick={() => doFinalize(pin)} loading={busy} disabled={!pin} data-testid="opname-pin-authorize"><ShieldCheck size={15} /> {t("Authorize")}</Button>
          </>}>
          <div className="space-y-3" data-testid="opname-pin-modal">
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              {active.opname_type === "opening"
                ? t("Finalizing a First Opening Stock Take requires the Owner PIN. This becomes the official opening inventory valuation.")
                : t("Finalizing a Correction Reset requires the Owner PIN. A new inventory baseline is created — history stays untouched.")}
            </div>
            <Input label={t("Enter Owner PIN")} type="password" inputMode="numeric" value={pin} autoFocus
              onChange={(e) => setPin(e.target.value)} placeholder="••••" data-testid="opname-pin-input" />
          </div>
        </Modal>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Stock Take")} subtitle={isBranch ? t("Count your branch stock vs the system") : t("Physical stock count vs system — central & branch")} icon={ClipboardCheck}>
        {canEdit && <Button onClick={() => setStartModal(true)} data-testid="opname-start-btn"><Plus size={18} /> {t("New Stock Take")}</Button>}
      </PageHeader>

      {canEdit && (() => {
        const draft = sessions.find((s) => s.status === "DRAFT");
        const latestFinal = sessions.find((s) => s.status === "FINALIZED");
        const state = draft ? "continue" : (latestFinal ? "view" : "start");
        const cfg = {
          start: { title: t("Start Stock Take"), sub: isBranch ? t("Reconcile your branch stock against the system.") : t("Physical stock count vs system — central & branch"), btn: t("Start Stock Take"), action: () => setStartModal(true) },
          continue: { title: t("Continue Stock Take"), sub: t("You have a stock take in progress. Continue where you left off."), btn: t("Continue Stock Take"), action: () => openSession(draft.id) },
          view: { title: t("View Result"), sub: t("Your latest stock take is complete. View the result or start a new one."), btn: t("View Result"), action: () => openSession(latestFinal.id) },
        }[state];
        return (
          <Card className="mb-5 flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="opname-cta-banner">
            <div className="flex items-start gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-white"><ClipboardCheck size={24} /></span>
              <div>
                <p className="font-heading text-lg font-extrabold text-ink" data-testid="opname-cta-title">{cfg.title}</p>
                <p className="text-xs text-ink-mute">{cfg.sub}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {state === "view" && <Button variant="outline" onClick={() => setStartModal(true)} data-testid="opname-cta-new">{t("Start a New Stock Take")}</Button>}
              <Button onClick={cfg.action} data-testid="opname-cta-primary">{cfg.btn}</Button>
            </div>
          </Card>
        );
      })()}

      {sessions.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute">{t("No stock takes yet. Start one to reconcile physical stock.")}</Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {sessions.map((s) => (
            <Card key={s.id} className="cursor-pointer p-5 transition-all hover:-translate-y-1 hover:shadow-cardhover" onClick={() => openSession(s.id)} data-testid={`opname-card-${s.opname_no}`}>
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className="inline-flex min-w-0 items-center gap-2">
                  {s.scope === "central" ? <Warehouse size={18} className="shrink-0 text-primary" /> : <Store size={18} className="shrink-0 text-primary" />}
                  <span className="truncate font-heading text-base font-extrabold text-ink" data-testid={`opname-branch-${s.opname_no}`}>{s.branch_name}</span>
                </span>
                <Badge tone={s.status === "FINALIZED" ? "success" : "warn"}>{s.status === "FINALIZED" ? t("Done") : t("In Process")}</Badge>
              </div>
              <p className="flex items-center gap-2 text-xs text-ink-soft">
                <CalendarDays size={13} className="text-ink-mute" /> {fmtWIB(s.created_at)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <TypeBadge type={s.opname_type} />
                {s.status === "FINALIZED" && s.accuracy_pct != null && <Badge tone={s.accuracy_pct >= 95 ? "success" : s.accuracy_pct >= 80 ? "warn" : "danger"}>{t("Accuracy")} {s.accuracy_pct}%</Badge>}
                {s.pending_count > 0 && <Badge tone="warn">{s.pending_count} {t("pending")}</Badge>}
              </div>
              <p className="mt-3 text-[11px] text-ink-mute" data-testid={`opname-docid-${s.opname_no}`}>{t("By")} {s.created_by_name} · {t("Document ID")}: {s.opname_no}</p>
            </Card>
          ))}
        </div>
      )}

      <Modal open={startModal} onClose={() => setStartModal(false)} title={t("Start Stock Take")}
        footer={<><Button variant="outline" onClick={() => setStartModal(false)}>{t("Cancel")}</Button><Button onClick={startOpname} loading={busy} data-testid="opname-start-confirm">{t("Start Count")}</Button></>}>
        {!isBranch && (
          <>
            <Select label={t("Scope")} value={startForm.scope} onChange={(e) => setStartForm({ ...startForm, scope: e.target.value })} className="mb-3" data-testid="opname-scope">
              <option value="central">{t("Central Warehouse")}</option>
              <option value="branch">{t("Branch Inventory")}</option>
            </Select>
            {startForm.scope === "branch" && (
              <Select label={t("Branch")} value={startForm.branch_id} onChange={(e) => setStartForm({ ...startForm, branch_id: e.target.value })} className="mb-3" data-testid="opname-branch">
                <option value="">{t("Select branch...")}</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            )}
          </>
        )}
        {isBranch && <p className="mb-2 rounded-lg bg-primary-light/50 px-3 py-2 text-sm font-medium text-primary">{t("This counts your own branch inventory.")}</p>}

        {/* Stock Take type picker */}
        {(() => {
          const targetEntity = isBranch ? (user?.branch_id || "central")
            : (startForm.scope === "branch" ? startForm.branch_id : "central");
          const openingDone = !!(targetEntity && meta.opening_done[targetEntity]);
          const types = [
            { id: "regular", label: t("Regular Stock Take"), desc: t("Daily / monthly operations. No authorization required."), show: true, disabled: false },
            { id: "opening", label: t("First Opening Stock Take"), desc: openingDone ? t("Already executed for this location (one per location).") : t("One-time opening count before Go-Live. Requires Owner PIN."), show: meta.implementation_mode, disabled: openingDone },
            { id: "correction_reset", label: t("Correction Reset Stock Take"), desc: t("Creates a new inventory baseline. History stays untouched. Requires Owner PIN + reason."), show: true, disabled: false },
          ].filter((x) => x.show);
          return (
            <div className="mb-1 space-y-2" data-testid="opname-type-picker">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-mute">{t("Stock Take Type")}</p>
              {types.map((x) => (
                <label key={x.id} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-sm transition-colors ${x.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${startForm.opname_type === x.id ? "border-primary bg-primary/5" : "border-line"}`} data-testid={`opname-type-${x.id}`}>
                  <input type="radio" name="opnameType" className="mt-0.5" disabled={x.disabled}
                    checked={startForm.opname_type === x.id}
                    onChange={() => setStartForm({ ...startForm, opname_type: x.id })} />
                  <span>
                    <span className="font-semibold text-ink">{x.label}</span>
                    <p className="text-xs text-ink-mute">{x.desc}</p>
                  </span>
                </label>
              ))}
              {["opening", "correction_reset"].includes(startForm.opname_type) && (
                <Input label={startForm.opname_type === "correction_reset" ? t("Reason (required)") : t("Reason (optional)")}
                  value={startForm.reason} onChange={(e) => setStartForm({ ...startForm, reason: e.target.value })}
                  placeholder={t("e.g. inventory accuracy became unreliable")} data-testid="opname-type-reason" />
              )}
            </div>
          );
        })()}
        <p className="mt-3 text-xs text-ink-soft">{t("A snapshot of current system quantities will be captured. Enter physical counts, then finalize.")} {isBranch ? t("Variances over ±5% are sent to HQ for approval.") : t("Adjustments + ledger entries are generated automatically.")}</p>
      </Modal>
    </div>
  );
}
